import {useEffect} from 'react';

import isArchived from 'archive/isArchived';
import BenchlingConfig from 'BenchlingConfig';
import {PublishingRecordForVersionHistoryPanel} from 'template-collectibles/publishing-ui/graphql/types';

import {ProcedureMetadata} from '../ProcedureDomainTypes';
import {
  isFeatureEnabledForProcedureType,
  ProceduresFeature,
} from '../utils/ProcedureCapabilityManagementHelpers';
import getBannerForReadOnlyProcedure from './getBannerForReadOnlyProcedure';
import {ProcedureAccessLevel} from './validation/determineProcedureAccessLevel';

export interface UseProcedureReadonlyStateParams {
  procedureMetadata: ProcedureMetadata;
  accessLevel: ProcedureAccessLevel | null;
  legacyFullyBlockedOnServerError: boolean;
  currentFlowchartVersionMetadata: {id: string; createdAt: string; isLatest: boolean};
  publishingRecords: Array<PublishingRecordForVersionHistoryPanel>;
  readonlyState: [boolean, (isReadonly: boolean) => void];
  legacyIsAutosaverBlockedState: [boolean, (isBlocked: boolean) => void];
  legacyIsSaveInFlightState: [boolean, (isSaveInFlight: boolean) => void];
}

export interface UseProcedureReadonlyStateReturn {
  readOnlyBannerMessage: string | null;
  isProcedureReadOnly: boolean;
  isProcedureArchived: boolean;
}

/**
 * Hook that manages the read-only state of a procedure, including:
 * - Computing whether the procedure should be read-only
 * - Generating the appropriate banner message for read-only states
 * - Managing autosave blocking for legacy procedure editor based on read-only conditions
 */
export default function useProcedureReadonlyState({
  procedureMetadata,
  accessLevel,
  legacyFullyBlockedOnServerError,
  currentFlowchartVersionMetadata,
  publishingRecords,
  readonlyState,
  legacyIsAutosaverBlockedState,
  legacyIsSaveInFlightState,
}: UseProcedureReadonlyStateParams): UseProcedureReadonlyStateReturn {
  const [isProcedureReadOnly, setProcedureReadonly] = readonlyState;
  const [_legacyShouldBlockAutosave, setLegacyShouldBlockAutosave] = legacyIsAutosaverBlockedState;
  const [_legacyIsSaving, setLegacyIsSaving] = legacyIsSaveInFlightState;

  const isProcedureArchived = isArchived(procedureMetadata);
  const hasAccessToEditConfig = isFeatureEnabledForProcedureType(
    procedureMetadata.procedureType,
    ProceduresFeature.EDIT_CONFIG
  );
  const isViewingPublishedVersion =
    BenchlingConfig.PROCEDURES_ENABLE_PUBLISHING &&
    null != publishingRecords.find((pr) => pr.publishableVersionId === currentFlowchartVersionMetadata.id);

  const readOnlyBannerMessage = getBannerForReadOnlyProcedure({
    procedureType: procedureMetadata.procedureType,
    legacyFullyBlockedOnServerError,
    isLatestFlowchartVersion: currentFlowchartVersionMetadata.isLatest,
    isViewingPublishedVersion,
    isProcedureArchived,
    isWritable: procedureMetadata.permissions.writable,
    hasAccessToEditConfig,
    accessLevel,
  });
  const newIsProcedureReadOnly = readOnlyBannerMessage != null;

  // Update readonly state when it changes
  if (isProcedureReadOnly !== newIsProcedureReadOnly) {
    setProcedureReadonly(newIsProcedureReadOnly);
  }

  // Manage autosave blocking based on various conditions
  useEffect(() => {
    if (legacyFullyBlockedOnServerError) {
      setLegacyIsSaving(false);
    }
    setLegacyShouldBlockAutosave(
      legacyFullyBlockedOnServerError || isProcedureArchived || !hasAccessToEditConfig || isProcedureReadOnly
    );
  }, [
    legacyFullyBlockedOnServerError,
    isProcedureArchived,
    hasAccessToEditConfig,
    isProcedureReadOnly,
    setLegacyIsSaving,
    setLegacyShouldBlockAutosave,
  ]);

  return {
    readOnlyBannerMessage,
    isProcedureReadOnly: newIsProcedureReadOnly,
    isProcedureArchived,
  };
}
